import {Button, Popover} from '@primer/react'
import {useCallback, useState} from 'react'

import {useUpdateUserDiffViewPreferenceMutation} from '../hooks/mutations/use-update-user-diff-view-preference-mutation'

import styles from './DiffCompactLinesPopover.module.css'
import {clsx} from 'clsx'

/** Canonical user-notice name consumers should pass to their notice hooks. */
export const COMPACT_DIFF_LINES_NOTICE_NAME = 'compact_diff_lines'

type DiffCompactLinesPopoverProps = {
  /** Whether the `compact_diff_lines` notice has already been dismissed. */
  isNoticeDismissed: boolean
  /** Dismisses the `compact_diff_lines` notice. */
  onDismissNotice: () => void
}

export function DiffCompactLinesPopover({isNoticeDismissed, onDismissNotice}: DiffCompactLinesPopoverProps) {
  const {mutate: updateUserDiffViewPreference} = useUpdateUserDiffViewPreferenceMutation({
    onSuccess: () => {},
    onError: () => {},
  })
  const [popoverOpen, setPopoverOpen] = useState(true)

  const onEnable = useCallback(() => {
    updateUserDiffViewPreference({lineSpacing: 'compact'})
    setPopoverOpen(false)
    onDismissNotice()
  }, [onDismissNotice, updateUserDiffViewPreference])

  const onDismiss = useCallback(() => {
    setPopoverOpen(false)
    onDismissNotice()
  }, [onDismissNotice])

  if (isNoticeDismissed) return null

  return (
    <Popover open={popoverOpen} caret="top-right" className={styles.PopoverContainer}>
      <Popover.Content className={clsx('d-flex flex-column gap-2', styles.PopoverContent)}>
        <h2 className={styles.Heading}>Customizable line height</h2>
        <p>
          The default line height has been increased for improved accessibility. You can choose to enable a more compact
          line height from the view settings menu.
        </p>
        <div className="d-flex gap-2 flex-row w-full flex-wrap">
          <Button onClick={onEnable}>Enable compact line height</Button>
          <Button onClick={onDismiss} variant="invisible">
            Dismiss
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  )
}
